import { forwardRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  Slide,
  Toolbar,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { IconArrowLeft } from "@tabler/icons-react";
import { FaMedal } from "react-icons/fa6";
import { Capacitor } from "@capacitor/core";

import ProgressPage from "features/progress";
import useCustomNavigation from "hooks/customNavigation";
import { useLayout } from "features/layout/LayoutContext";

// The overlay covers the tab bar but the fixed AdBanner still draws at the very
// bottom, so reserve its height (max, since we don't know here whether it shows)
// plus the safe-area inset -- this keeps the last profile content above the ad.
const AD_BANNER_HEIGHT = Capacitor.getPlatform() === "ios" ? 66 : 50;

// A user-profile "page" pushed over the current list. It lives on a react-router
// history entry (same URL, extra `state.profileStack`) rather than a route swap,
// so the list underneath is never unmounted or re-rendered -- opening/closing a
// profile is a pure push/pop. Dismissal is a single history-back (iOS backswipe /
// Android hardware back both produce one), which shrinks the stack and closes it.

export interface ProfileEntry {
  uid?: string | null;
  username?: string | null;
}

const readStack = (state: unknown): ProfileEntry[] =>
  (state as { profileStack?: ProfileEntry[] } | null)?.profileStack ?? [];

/** Push a profile over the current page. Same pathname => the list stays mounted. */
export function useOpenProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  return (entry: ProfileEntry) => {
    const stack = readStack(location.state);
    navigate(location.pathname + location.search, {
      state: { ...(location.state as object), profileStack: [...stack, entry] },
    });
  };
}

const SlideUp = forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProgressOverlayHost = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const appNavigate = useCustomNavigation();
  const { showAds } = useLayout();
  const stack = readStack(location.state);
  const top = stack[stack.length - 1];

  return (
    <Dialog
      open={stack.length > 0}
      fullScreen
      // No onClose: dismissal is backswipe / hardware back only (history-driven).
      TransitionComponent={SlideUp}
      keepMounted={false}
      // The scroller is <main>, not <body>, so body scroll-lock is unneeded and
      // its scrollbar-width padding would nudge the list underneath on open/close.
      disableScrollLock
    >
      {top && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            // Match the app's page background so it isn't jarring white-on-white.
            bgcolor: "primary.light",
            // Anchor for the ad backing strip below.
            position: "relative",
          }}
        >
          {/* A back affordance for users who don't realize they can swipe back.
              navigate(-1) is the same history-back that a swipe produces. */}
          <AppBar
            position="static"
            color="inherit"
            elevation={0}
            sx={{
              flex: "0 0 auto",
              bgcolor: "background.default",
              pt: "env(safe-area-inset-top, 0px)",
            }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                aria-label="Back"
                onClick={() => navigate(-1)}
              >
                <IconArrowLeft />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Box
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              px: "12px",
              pt: "12px",
              // Keep the last content above the fixed ad banner at the screen bottom.
              pb: `calc(${AD_BANNER_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 12px)`,
            }}
          >
            {/* Key by target user so a nested push swaps to a fresh, correctly
                loaded profile instead of reusing the previous one's state. */}
            <ProgressPage
              key={top.username || top.uid || "profile"}
              overlayEntry={top}
            />
          </Box>

          {/* Ad backing strip: the global AdBanner sits below this full-screen
              Dialog, so the native ad would otherwise draw straight onto the
              profile content. Re-create the same paper backing + Support button
              here (non-supporters only) so the ad has a proper footer to sit on. */}
          {showAds && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: `${AD_BANNER_HEIGHT}px`,
                borderTop: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                px: 2,
              }}
            >
              <Button
                startIcon={<FaMedal />}
                onClick={() => appNavigate("support")}
                color="warning"
                variant="contained"
                size="small"
              >
                Support this app to keep it running!
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default ProgressOverlayHost;
